import React, { useEffect, useMemo, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { ink, surface, line, radius, shadow, font, transition, bp } from 'features/dashboard/tokens'
import { toast } from 'react-toastify'
import CloseRoundedIcon              from '@mui/icons-material/CloseRounded'
import CheckBoxRoundedIcon           from '@mui/icons-material/CheckBoxRounded'
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded'
import BlockRoundedIcon              from '@mui/icons-material/BlockRounded'
import { fetchDashboardScopeOptions } from 'services/BpService'
import { bulkSetObligationOverride }   from 'services/BpService'
import { bulkApplyObligations }        from 'services/BpService'
import SearchableSelect from 'components/SearchableSelect'
import { severityColour } from 'features/dashboard/charts/scoreColour'

/**
 * Bulk override wizard. The user picks ONE library obligation, then selects
 * many buildings to apply "not applicable" to in a single transaction.
 *
 * UX:
 *   Step 1 — pick obligation (from the dashboard's missing obligations list)
 *   Step 2 — pick buildings (multi-select with select-all / search / filter)
 *   Step 3 — confirm reason → apply
 *   Step 4 — done, score updates
 *
 * Props:
 *   open: bool
 *   missingObligations: dashboard payload
 *   onClose: () => void
 *   onApplied: () => void  — fired after apply so the dashboard can refetch
 */
const BulkActionWizard = ({ open, missingObligations = [], onClose, onApplied }) => {
  const [step, setStep] = useState(1)
  const [selectedSlug, setSelectedSlug] = useState(null)
  const [selectedBps, setSelectedBps] = useState(() => new Set())
  const [bps, setBps] = useState([])
  const [bpSearch, setBpSearch] = useState('')
  const [reason, setReason] = useState('')
  const [applying, setApplying] = useState(false)
  const [result, setResult] = useState(null)
  const [mode, setMode] = useState('override')
  const [cadenceDays, setCadenceDays] = useState('')
  const [criticality, setCriticality] = useState('')
  const [applyAllUnits, setApplyAllUnits] = useState(false)

  useEffect(() => {
    if (!open) return
    setStep(1); setSelectedSlug(null); setSelectedBps(new Set()); setReason(''); setResult(null); setBpSearch(''); setMode('override'); setCadenceDays(''); setCriticality(''); setApplyAllUnits(false)
    fetchDashboardScopeOptions()
      .then(res => setBps(res.data?.data?.buildingPassports || []))
      .catch(() => {})
  }, [open])

  const selectedObligation = useMemo(
    () => missingObligations.find(o => o.slug === selectedSlug),
    [missingObligations, selectedSlug],
  )

  const filteredBps = useMemo(() => {
    const q = bpSearch.trim().toLowerCase()
    if (!q) return bps
    return bps.filter(b => (b.name || '').toLowerCase().includes(q))
  }, [bps, bpSearch])

  const toggleBp = id => {
    setSelectedBps(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    setSelectedBps(prev => {
      if (prev.size === filteredBps.length) return new Set()
      return new Set(filteredBps.map(b => b.id))
    })
  }

  const apply = () => {
    if (!selectedSlug || selectedBps.size === 0) return
    setApplying(true)
    const request = mode === 'configure'
      ? bulkApplyObligations({
          buildingIds: Array.from(selectedBps),
          decisions: [{
            librarySlug: selectedSlug,
            ...(cadenceDays ? { cadenceDays: Number(cadenceDays) } : {}),
            ...(criticality ? { criticality } : {}),
            applyToAllUnits: applyAllUnits,
          }],
        })
      : bulkSetObligationOverride({
          librarySlug: selectedSlug,
          buildingPassportIds: Array.from(selectedBps),
          reason: reason.trim() || null,
        })
    request
      .then(res => {
        const data = res.data?.data || {}
        setResult(data)
        setStep(4)
        toast.success(`Applied to ${data.applied} Building Passports`)
        onApplied?.()
      })
      .catch(() => toast.error('Could not apply. Try again.'))
      .finally(() => setApplying(false))
  }

  if (!open) return null

  return (
    <Backdrop onClick={onClose}>
      <Card onClick={e => e.stopPropagation()}>
        <CloseBtn onClick={onClose}><CloseRoundedIcon style={{ fontSize: 18 }} /></CloseBtn>

        <StepRail>
          <StepDot $active={step >= 1} $done={step > 1}>1</StepDot>
          <StepLine $on={step > 1} />
          <StepDot $active={step >= 2} $done={step > 2}>2</StepDot>
          <StepLine $on={step > 2} />
          <StepDot $active={step >= 3} $done={step > 3}>3</StepDot>
        </StepRail>

        {step === 1 && (
          <>
            <Eyebrow>Bulk update · Step 1 of 3</Eyebrow>
            <Title>Which obligation?</Title>
            <Subtitle>Pick one, then choose the Building Passports and what to apply.</Subtitle>
            <ObligationList>
              {missingObligations.map(o => (
                <ObligationRow
                  key={o.slug}
                  $active={selectedSlug === o.slug}
                  onClick={() => setSelectedSlug(o.slug)}
                >
                  <ObligationCheck $active={selectedSlug === o.slug}>
                    {selectedSlug === o.slug
                      ? <CheckBoxRoundedIcon style={{ fontSize: 18, color: '#33becc' }} />
                      : <CheckBoxOutlineBlankRoundedIcon style={{ fontSize: 18, color: line.strong }} />}
                  </ObligationCheck>
                  <ObligationName>
                    {o.name}
                    <ObligationFramework>{o.regulatoryFramework}</ObligationFramework>
                  </ObligationName>
                  <ObligationCount>missing on {o.bpCount}</ObligationCount>
                </ObligationRow>
              ))}
            </ObligationList>
            <Actions>
              <Cancel onClick={onClose}>Cancel</Cancel>
              <Primary disabled={!selectedSlug} onClick={() => setStep(2)}>
                Next → pick Building Passports
              </Primary>
            </Actions>
          </>
        )}

        {step === 2 && (
          <>
            <Eyebrow>Bulk update · Step 2 of 3</Eyebrow>
            <Title>Which Building Passports?</Title>
            <Subtitle>
              {selectedObligation?.name && (
                <>Selected obligation: <strong>{selectedObligation.name}</strong></>
              )}
            </Subtitle>
            <SearchRow>
              <SearchInput
                placeholder='Search Building Passports…'
                value={bpSearch}
                onChange={e => setBpSearch(e.target.value)}
              />
              <SelectAll onClick={toggleAll}>
                {selectedBps.size === filteredBps.length && filteredBps.length > 0
                  ? 'Clear all'
                  : 'Select all'}
              </SelectAll>
            </SearchRow>
            <BpList>
              {filteredBps.map(b => (
                <BpRow
                  key={b.id}
                  $active={selectedBps.has(b.id)}
                  onClick={() => toggleBp(b.id)}
                >
                  {selectedBps.has(b.id)
                    ? <CheckBoxRoundedIcon style={{ fontSize: 18, color: '#33becc' }} />
                    : <CheckBoxOutlineBlankRoundedIcon style={{ fontSize: 18, color: line.strong }} />}
                  <BpName>{b.name}</BpName>
                </BpRow>
              ))}
            </BpList>
            <SelectedCount>{selectedBps.size} selected</SelectedCount>
            <Actions>
              <Cancel onClick={() => setStep(1)}>← Back</Cancel>
              <Primary disabled={selectedBps.size === 0} onClick={() => setStep(3)}>
                Next → add reason
              </Primary>
            </Actions>
          </>
        )}

        {step === 3 && (
          <>
            <Eyebrow>Bulk update · Step 3 of 3</Eyebrow>
            <Title>What do you want to apply?</Title>
            <ModeToggle>
              <ModeBtn $active={mode === 'configure'} onClick={() => setMode('configure')}>
                Configure
              </ModeBtn>
              <ModeBtn $active={mode === 'override'} onClick={() => setMode('override')}>
                Mark not applicable
              </ModeBtn>
            </ModeToggle>

            {mode === 'configure' ? (
              <>
                <Subtitle>
                  Set <strong>{selectedObligation?.name}</strong> across{' '}
                  <strong>{selectedBps.size} Building Passports</strong>. Only buildings that have this
                  obligation are affected.
                </Subtitle>
                <Field>
                  <FieldLabel>Cadence</FieldLabel>
                  <SearchableSelect
                    value={cadenceDays || null}
                    onChange={v => setCadenceDays(v || '')}
                    options={[
                      { value: '30', label: 'Monthly' },
                      { value: '91', label: 'Quarterly' },
                      { value: '182', label: 'Every 6 months' },
                      { value: '365', label: 'Annual' },
                      { value: '730', label: 'Every 2 years' },
                      { value: '1825', label: 'Every 5 years' },
                    ]}
                    clearable
                    clearLabel='Leave unchanged'
                    placeholder='Leave unchanged'
                    ariaLabel='Cadence'
                  />
                </Field>
                <Field>
                  <FieldLabel>Criticality</FieldLabel>
                  <SearchableSelect
                    value={criticality || null}
                    onChange={v => setCriticality(v || '')}
                    options={[
                      { value: 'low', label: 'Low', color: severityColour('low') },
                      { value: 'medium', label: 'Medium', color: severityColour('medium') },
                      { value: 'high', label: 'High', color: severityColour('high') },
                      { value: 'critical', label: 'Critical', color: severityColour('critical') },
                    ]}
                    clearable
                    clearLabel='Leave unchanged'
                    placeholder='Leave unchanged'
                    ariaLabel='Criticality'
                  />
                </Field>
                <CheckRow onClick={() => setApplyAllUnits(v => !v)}>
                  {applyAllUnits
                    ? <CheckBoxRoundedIcon style={{ fontSize: 18, color: '#33becc' }} />
                    : <CheckBoxOutlineBlankRoundedIcon style={{ fontSize: 18, color: line.strong }} />}
                  <CheckText>Apply to all units in each building (tracks every Unit Passport)</CheckText>
                </CheckRow>
              </>
            ) : (
              <>
                <Subtitle>
                  You're about to mark <strong>{selectedObligation?.name}</strong> as not applicable on{' '}
                  <strong>{selectedBps.size} Building Passports</strong>. The reason will be stamped on the audit
                  trail for future reviewers.
                </Subtitle>
                <ReasonInput
                  autoFocus
                  placeholder='e.g. "No gas supply on any of these residential schemes"'
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  maxLength={1000}
                />
              </>
            )}

            <Actions>
              <Cancel onClick={() => setStep(2)}>← Back</Cancel>
              <Primary
                disabled={applying || (mode === 'configure' && !cadenceDays && !criticality && !applyAllUnits)}
                onClick={apply}
                $danger={mode === 'override'}
              >
                {applying
                  ? 'Applying…'
                  : <>{mode === 'override' && <BlockRoundedIcon style={{ fontSize: 16 }} />} Apply to {selectedBps.size} Building Passports</>}
              </Primary>
            </Actions>
          </>
        )}

        {step === 4 && result && (
          <>
            <DoneTitle>Applied to {result.applied} Building Passports</DoneTitle>
            <DoneText>
              {result.obligationsUpdated != null && `${result.obligationsUpdated} obligations updated · `}
              {result.applied === selectedBps.size
                ? 'All your selections were applied. Score will update on next dashboard refresh.'
                : `Applied to ${result.applied} of ${result.requested} selected.${result.rejected > 0 ? ` ${result.rejected} weren't accessible.` : ''}`}
            </DoneText>
            <Actions>
              <Primary onClick={onClose}>Done</Primary>
            </Actions>
          </>
        )}
      </Card>
    </Backdrop>
  )
}

export default BulkActionWizard

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(12, 18, 41, 0.55);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 1300; padding: 24px;
`

const Card = styled.div`
  position: relative;
  width: 100%; max-width: ${bp.mobile};
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${radius.md};
  padding: 28px 32px 24px;
  box-shadow: ${shadow.overlay};
  animation: ${slideIn} 260ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
  max-height: 90vh;
  overflow-y: auto;
`

const CloseBtn = styled.button`
  position: absolute; top: 14px; right: 14px;
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  background: rgba(12,18,41,0.06);
  border: none; border-radius: ${radius.round};
  cursor: pointer; color: ${({ theme }) => theme.colors.haiti};
  &:hover { background: rgba(12,18,41,0.14); }
`

const StepRail = styled.div`
  display: flex; align-items: center; gap: 6px;
  margin-bottom: 18px;
`

const ModeToggle = styled.div`
  display: inline-flex; gap: 4px;
  padding: 4px;
  background: ${surface.wash};
  border-radius: ${radius.round};
  margin-bottom: 16px;
`

const ModeBtn = styled.button`
  font: inherit;
  font-size: ${font.body};
  font-weight: 700;
  padding: 7px 16px;
  border: none; border-radius: ${radius.round};
  cursor: pointer;
  background: ${({ $active, theme }) => $active ? theme.colors.white : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.haiti : theme.colors.grey[800]};
`

const Field = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${surface.wash};
`

const FieldLabel = styled.span`
  font-size: ${font.body};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const CheckRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 14px 0 4px;
  cursor: pointer;
`

const CheckText = styled.span`
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[800]};
`

const StepDot = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 26px;
  border-radius: ${radius.round};
  font-size: ${font.small}; font-weight: 800;
  background: ${({ $done, $active }) => $done ? '#33becc' : $active ? ink.base : surface.wash};
  color: ${({ $done, $active }) => $done || $active ? '#fff' : ink.mute};
  border: 1px solid ${({ $done, $active }) => $done ? '#33becc' : $active ? ink.base : line.base};
`

const StepLine = styled.span`
  flex: 1; height: 2px;
  background: ${({ $on }) => $on ? '#33becc' : surface.wash};
  border-radius: ${radius.xs};
`

const Eyebrow = styled.div`
  font-size: ${font.caption};
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
  margin-bottom: 6px;
`

const Title = styled.h2`
  margin: 0 0 6px;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.title};
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.2;
`

const Subtitle = styled.p`
  margin: 0 0 16px;
  font-size: ${font.body};
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const ObligationList = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  margin-bottom: 16px;
  max-height: 320px;
  overflow-y: auto;
`

const ObligationRow = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 14px;
  background: ${({ $active }) => $active ? 'rgba(51,190,204,0.10)' : surface.wash};
  border: 1px solid ${({ $active }) => $active ? '#33becc' : line.base};
  border-radius: ${radius.md};
  cursor: pointer;
  transition: ${transition};
  &:hover { border-color: ${line.strong}; }
`

const ObligationCheck = styled.span`
  display: inline-flex; flex-shrink: 0;
`

const ObligationName = styled.div`
  flex: 1; min-width: 0;
  display: flex; flex-direction: column; gap: 2px;
  font-size: ${font.body};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const ObligationFramework = styled.span`
  font-size: ${font.caption};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 700;
`

const ObligationCount = styled.span`
  font-size: ${font.caption};
  color: ${({ theme }) => theme.colors.grey[800]};
  white-space: nowrap;
`

const SearchRow = styled.div`
  display: flex; gap: 8px; align-items: center;
  margin-bottom: 10px;
`

const SearchInput = styled.input`
  flex: 1;
  font: inherit; font-size: ${font.body};
  padding: 8px 12px;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  background: ${({ theme }) => theme.colors.lighterGrey};
  &:focus { outline: none; border-color: #33becc; background: #fff; }
`

const SelectAll = styled.button`
  font: inherit; font-size: ${font.small}; font-weight: 700;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const BpList = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  padding: 6px;
`

const BpRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px;
  background: ${({ $active }) => $active ? 'rgba(51,190,204,0.06)' : 'transparent'};
  border-radius: ${radius.sm};
  cursor: pointer;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.haiti};
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const BpName = styled.span`
  flex: 1; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`

const SelectedCount = styled.div`
  margin-top: 10px;
  font-size: ${font.small};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  text-align: right;
`

const ReasonInput = styled.textarea`
  width: 100%; min-height: 100px; resize: vertical;
  font: inherit; font-size: ${font.body};
  padding: 12px 14px;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: none; border-color: #33becc; }
`

const Actions = styled.div`
  display: flex; justify-content: space-between; gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${line.base};
`

const Cancel = styled.button`
  font: inherit;
  padding: 10px 18px;
  background: transparent;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: ${font.body}; font-weight: 600;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lighterGrey}; }
`

const Primary = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 20px;
  background: ${({ $danger }) => $danger ? '#b00020' : ink.base};
  color: #fff;
  border: none; border-radius: ${radius.md};
  font-size: ${font.body}; font-weight: 700; letter-spacing: 0.02em;
  cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(1.10); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`

const DoneTitle = styled.h2`
  margin: 0 0 8px;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: ${font.display};
  color: ${({ theme }) => theme.colors.haiti};
`

const DoneText = styled.p`
  margin: 0;
  font-size: ${font.body};
  color: ${({ theme }) => theme.colors.grey[800]};
  line-height: 1.5;
`
